import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OuiButton } from 'omnium-ui/button';
import { OuiDialogModule } from 'omnium-ui/dialog';
import { OuiTextarea, OuiValidationError } from 'omnium-ui/form-field';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiLoading } from 'omnium-ui/loading';

export interface HabilitationActivationDialogData {
  habilitationName: string;
  mode?: HabilitationActivationDialogMode;
}

export type HabilitationActivationDialogMode = 'temporaryActivation' | 'reactivation';

@Component({
  selector: 'lib-habilitation-activation-dialog',
  standalone: true,
  imports: [OuiDialogModule, OuiButton, OuiIconModule, OuiLoading, OuiTextarea],
  templateUrl: './habilitation-activation-dialog.component.html',
  styleUrl: './habilitation-activation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HabilitationActivationDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<HabilitationActivationDialogComponent>
  );
  private readonly data: HabilitationActivationDialogData = inject(MAT_DIALOG_DATA);

  readonly habilitationName = this.data.habilitationName;
  readonly mode = this.data.mode ?? 'temporaryActivation';
  readonly title =
    this.mode === 'reactivation'
      ? `Réactivation de l'habilitation "${this.habilitationName}"`
      : `Activation temporaire de l'habilitation "${this.habilitationName}"`;
  readonly description =
    this.mode === 'reactivation'
      ? "Cette action annulera la désactivation manuelle en cours. L'habilitation redeviendra active selon ses règles de validité habituelles."
      : 'Cette action accordera des droits supplémentaires pour une durée de 24 heures. Une fois activée, cette habilitation ne pourra pas être interrompue avant son expiration automatique.';
  readonly motifHint =
    this.mode === 'reactivation'
      ? 'Précisez ici les raisons de la réactivation'
      : "Précisez ici les raisons de l'activation temporaire";
  readonly confirmLabel =
    this.mode === 'reactivation' ? 'Confirmer la réactivation' : "Confirmer l'activation";
  readonly isLoading = signal<boolean>(false);

  readonly motifControl = new FormControl<string>('', {
    nonNullable: true,
    validators: Validators.required,
  });

  readonly motifErrors: OuiValidationError[] = [
    { code: 'required', message: 'Le motif est obligatoire' },
  ];

  readonly form = new FormGroup({
    motif: this.motifControl,
  });

  activateHabilitation() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close(this.motifControl.value.trim());
  }
}
